"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { notification, Button, Tag, Space, Typography } from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import {
    selectMedicationReminder,
  selectMedicationStockReminder,
  dismissReminder,
  clearReminder,
  clearStockReminder,
} from "../../medications/redux/slices/medicationSlice";
import { takeDose } from "../../medications/redux/thunks/medicationThunks";
import { getCurrentUser } from "../../authPatient/redux/thunks/authThunks";
import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";
import Cookies from "js-cookie";
function normalizeReminder(r) {
  if (!r) return null;

  const raw = r?.reminder ? r.reminder : r;

  const medicationId = raw.medicationId || raw.id || raw.medId;
  const doseIndex = raw.doseIndex ?? raw.index ?? raw.dose ?? 0;
  const reminderTime = raw.reminderTime || raw.time || raw.scheduledAt || raw.date;

  const stockReminder = useSelector(selectMedicationStockReminder);

  useEffect(() => {
  if (!stockReminder) return;

  const key = `stock-${stockReminder.medicationId}`;

  api.warning({
    key,
    message: "Stock médicament presque terminé",
    description: (
      <div>
        <strong>{stockReminder.name}</strong>
        <br />
        {stockReminder.message ||
          `Il reste environ ${stockReminder.remainingDays} jour(s) de traitement.`}
      </div>
    ),
    duration: 0,
    placement: "topRight",
    btn: (
      <Button
        type="primary"
        onClick={() => {
          api.destroy(key);
          dispatch(clearStockReminder());
        }}
      >
        OK
      </Button>
    ),
  });
}, [stockReminder, api, dispatch]);

  if (!medicationId && medicationId !== 0) return null;

  return {
    medicationId: String(medicationId),
    doseIndex: Number(doseIndex),
    name: raw.name || "Medication",
    dosage: raw.dosage,
    reminderTime,
    dismissed: Boolean(raw.dismissed),
    _key: raw._key,
    raw,
  };
}

export default function MedicationToastBridge() {
  const dispatch = useDispatch();

  const reminder = useSelector(selectMedicationReminder);

  const currentUser = useSelector(
    (state) => state.auth?.currentUser || state.auth?.user || null
  );

  const effectiveUserId = useSelector(selectEffectiveUserId);
  const userId = effectiveUserId || currentUser?.id || currentUser?.pgId || currentUser?._id || null;

  const [api, contextHolder] = notification.useNotification();

  // anti double-open pour le toast (clé)
  const lastKeyRef = useRef(null);

  // ensure user loaded
  useEffect(() => {
    const token = Cookies.get("accessToken");
    if (token && !currentUser) dispatch(getCurrentUser());
  }, [dispatch, currentUser]);

  const openToast = useCallback(
    (r) => {
      const norm = normalizeReminder(r);
      if (!norm) return;

      const rt = norm.reminderTime ? new Date(norm.reminderTime) : null;

      const key =
        norm._key ||
        `${norm.medicationId}:${norm.doseIndex}:${rt?.toISOString?.() || "no-time"}`;

      // dedupe toast
      if (lastKeyRef.current === key) return;
      lastKeyRef.current = key;

      api.open({
        key,
        message: (
          <Space size={8} align="center">
            <ClockCircleOutlined />
            <span style={{ fontWeight: 700 }}>Medication reminder</span>
          </Space>
        ),

        description: (
          <div style={{ minWidth: 300 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <Typography.Text strong style={{ fontSize: 14 }}>
                {norm.name}
              </Typography.Text>

              <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                Due now
              </Tag>
            </div>

            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
              {typeof norm.dosage !== "undefined" && norm.dosage !== null && (
                <Tag>Dosage: {norm.dosage}</Tag>
              )}
              <Tag>Dose #{norm.doseIndex + 1}</Tag>
              <Tag icon={<ClockCircleOutlined />}>
                {rt
                  ? rt.toLocaleTimeString("fr-TN", { hour: "2-digit", minute: "2-digit" })
                  : "Time unknown"}
              </Tag>
            </div>

            {!userId && (
              <div style={{ marginTop: 10 }}>
                <Tag color="warning" style={{ whiteSpace: "normal" }}>
                  Session not loaded yet — “Taken” will be enabled automatically.
                </Tag>
              </div>
            )}
          </div>
        ),

        duration: 0,
        placement: "topRight",
        getContainer: () => document.body,
        style: {
          zIndex: 2147483647,
          borderRadius: 14,
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          padding: "12px 14px",
        },

        btn: (
          <Space>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={!userId}
              onClick={async () => {
                if (!userId) return;

                try {
                  await dispatch(
                    takeDose({ userId, id: norm.medicationId, index: norm.doseIndex })
                  ).unwrap();

                  api.success({
                    message: "Marked as taken",
                    description: `${norm.name} (dose ${norm.doseIndex + 1})`,
                    placement: "topRight",
                    getContainer: () => document.body,
                    duration: 2,
                    style: { zIndex: 2147483647 },
                  });

                  api.destroy(key);
                  dispatch(clearReminder()); // ✅ clear => message card disparaît
                } catch (e) {
                  api.error({
                    message: "Failed",
                    description: e?.message || "Could not mark dose as taken",
                    placement: "topRight",
                    getContainer: () => document.body,
                    duration: 2,
                    style: { zIndex: 2147483647 },
                  });
                }
              }}
            >
              Taken
            </Button>

            <Button
              icon={<CloseCircleOutlined />}
              onClick={() => {
                // ✅ Dismiss => ferme toast, mais active le message card
                api.destroy(key);
                dispatch(dismissReminder());
              }}
            >
              Dismiss
            </Button>
          </Space>
        ),

        onClose: () => {
          // X close => pareil que dismiss
          dispatch(dismissReminder());
        },
      });
    },
    [api, dispatch, userId]
  );

  useEffect(() => {
    if (!reminder) return;

    // ✅ si dismissed => ne jamais rouvrir le toast
    if (reminder.dismissed) return;

    openToast(reminder);
  }, [reminder, openToast]);

  return <>{contextHolder}</>;
}
